import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { numberUtils } from '@pukka/common/utils';
import { Shimmer, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';

interface BillingPriceItemProps {
  readonly title?: string;
  readonly subTitle?: string;
  readonly note?: string;
  readonly originalPrice?: number;
  readonly finalPrice?: number;
  readonly priceSuffix?: string;
  readonly loading?: boolean;
  readonly children?: ReactNode;
  readonly href?: ReactNode;
  readonly className?: string;
  readonly hidePrice?: boolean;
}

function BillingPriceItem({
  title,
  subTitle,
  note,
  originalPrice = 0,
  finalPrice = 0,
  priceSuffix,
  loading,
  children,
  href,
  className,
  hidePrice,
}: BillingPriceItemProps) {
  return (
    <div className={twMerge('grid min-h-[48px] grid-flow-col grid-cols-[54px_1fr_max-content] items-center gap-4 p-2 md:px-8', className)}>
      {children}
      <div>
        {loading ? (
          <Shimmer size="md" />
        ) : (
          <Text variant="subtitle-2" weight="medium" color={href ? 'blue' : 'black'} className={twMerge('mb-0.5', href ? 'underline' : '')}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {href ? typeof href === 'string' ? <Link to={href}>{title}</Link> : href : title}
          </Text>
        )}
        {loading ? (
          <Shimmer size="sm" className="mt-1.5" />
        ) : (
          <Text variant="caption-2" color="neutral" className="text-sm">
            {subTitle}
          </Text>
        )}
        {note && (
          <Text variant="caption-2" color="secondary" className="text-sm">
            {note}
          </Text>
        )}
      </div>
      {!hidePrice && (
        <div className="text-right">
          {loading ? (
            <Shimmer size="md" className="ml-auto w-32" />
          ) : (
            <Text variant="subtitle-2" data-testid="item-final-price" className="text-neutral-700">
              Rp {numberUtils.formatCurrency(finalPrice)}
              <Text as="span" variant="caption-1" className="text-xs">
                {priceSuffix}
              </Text>
            </Text>
          )}
          {/* eslint-disable-next-line no-nested-ternary */}
          {originalPrice > finalPrice ? (
            loading ? (
              <Shimmer size="sm" className="ml-auto" />
            ) : (
              <Text variant="caption-2" className="text-red-500 line-through" data-testid="item-original-price">
                Rp {numberUtils.formatCurrency(originalPrice)}
              </Text>
            )
          ) : null}
        </div>
      )}
    </div>
  );
}

export { BillingPriceItem };
